'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Sparkles, CheckCircle2 } from 'lucide-react';
import { clsx } from 'clsx';
import { GradientFrame } from '../ui/GradientFrame';
import Modal from '../ui/Modal';
import { SelectInput, SelectOption } from '../form/SelectInput';
import { TextInput } from '../form/TextInput';
import { SlideInToast } from '../ui/SlideInToast';
import PrivacyCheckbox from '../form/PrivacyCheckbox';
import type { EventItem } from '@/types/events';
import { initAndTrackOnce, trackCustom } from '@/lib/fbpixel';

const FB_PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID as string;
const GAS_ENDPOINT = import.meta.env.VITE_GAS_ENDPOINT as string;

interface EventSignupModalProps {
  open: boolean;
  onClose: () => void;
  events?: EventItem[];
  defaultEventId?: string;
  lockEvent?: boolean;
}

const CONTACT_OPTIONS = ['By phone', 'WhatsApp', 'Telegram', 'Viber'];

export default function EventSignupModal({
  open,
  onClose,
  events = [],
  defaultEventId,
  lockEvent = false,
}: EventSignupModalProps) {
  const firedRef = useRef(false); // Lead
  const openTrackedRef = useRef(false); // OpenForm / open_form
  const startedRef = useRef(false); // StartFillingForm / start_filling_form
  const safeEvents = events;

  const [agree, setAgree] = useState(false);
  const [eventId, setEventId] = useState<string>(defaultEventId ?? '');
  const [eventType, setEventType] = useState<string>('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [preferredChannel, setPreferredChannel] = useState<string>(''); // only ONE
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<null | { ok: boolean; msg: string }>(null);
  const [toastOpen, setToastOpen] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const currentEvent = useMemo(
    () => safeEvents.find((e) => e.id === eventId),
    [safeEvents, eventId]
  );

  const selectedEventLabel = useMemo(() => {
    if (eventId === '') return 'Select event';

    const pieces = [currentEvent?.type, currentEvent?.date]
      .filter(Boolean)
      .join(' | ');

    return `${currentEvent?.title ?? ''}${pieces ? ' — ' : ''}${pieces}`;
  }, [eventId, currentEvent]);

  useEffect(() => {
    setEventId(defaultEventId ?? '');
  }, [defaultEventId]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    if (open) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  // -------------------------
  // OPEN FORM (GA + PIXEL)
  // -------------------------
  useEffect(() => {
    if (open) {
      setTimeout(() => firstFieldRef.current?.focus(), 30);

      // --- Google Analytics: open_form ---
      if (typeof window !== 'undefined' && !openTrackedRef.current) {
        (window as any).gtag?.('event', 'open_form', {
          event_category: 'lead',
          event_label: selectedEventLabel || 'ZeroClub Modal',
          event_type: eventType || undefined,
        });
      }

      // --- Meta Pixel: OpenForm ---
      if (FB_PIXEL_ID && !openTrackedRef.current) {
        const eventIdForPixel =
          (window.crypto as Crypto | undefined)?.randomUUID?.() ?? String(Date.now());
        trackCustom('OpenForm', {
          eventID: eventIdForPixel,
          event_source_url: window.location.href,
          content_name: 'ZeroClub Signup Modal',
          eventLabel: selectedEventLabel || undefined,
          eventType: eventType || undefined,
        });
      }

      openTrackedRef.current = true;
    } else {
      setSubmitting(false);
      setSubmitStatus(null);
      setName('');
      setEmail('');
      setPhone('');
      setPreferredChannel('');
      setToastOpen(false);
      setEventId(defaultEventId ?? '');
      firedRef.current = false;
      openTrackedRef.current = false;
      startedRef.current = false;
      setAgree(false);
    }
  }, [open, defaultEventId, eventType, selectedEventLabel]);

  // -------------------------
  // START FILLING FORM (PIXEL + GA)
  // -------------------------
  useEffect(() => {
    const form = formRef.current;
    if (!open || !form || startedRef.current) return;

    const onFirstFocusIn = () => {
      if (startedRef.current) return;

      // GA: start_filling_form
      if (typeof window !== 'undefined') {
        (window as any).gtag?.('event', 'start_filling_form', {
          event_category: 'lead',
          event_label: selectedEventLabel || 'ZeroClub Modal',
          event_type: eventType || undefined,
        });
      }

      // Meta Pixel: StartFillingForm
      const eventIdForPixel =
        (window.crypto as Crypto | undefined)?.randomUUID?.() ?? String(Date.now());
      trackCustom('StartFillingForm', {
        eventID: eventIdForPixel,
        event_source_url: window.location.href,
        content_name: 'ZeroClub Signup Form',
        eventLabel: selectedEventLabel || undefined,
        eventType: eventType || undefined,
      });

      startedRef.current = true;
    };

    form.addEventListener('focusin', onFirstFocusIn, { once: true });
    return () => form.removeEventListener('focusin', onFirstFocusIn);
  }, [open, eventType, selectedEventLabel]);

  // iFrame callback
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const onLoad = () => {
      if (!submitting) return;
      setSubmitting(false);
      setSubmitStatus({ ok: true, msg: 'Application sent! We will be in touch soon.' });
      setToastOpen(true);

      // GA: submit_form
      if (typeof window !== 'undefined') {
        (window as any).gtag?.('event', 'submit_form', {
          event_category: 'lead',
          event_label: selectedEventLabel || 'ZeroClub Modal',
          event_type: eventType || undefined,
          submit_status: 'success_iframe',
        });
      }
    };

    iframe.addEventListener('load', onLoad);
    return () => {
      iframe.removeEventListener('load', onLoad);
    };
  }, [submitting, eventType, selectedEventLabel]);

  useEffect(() => {
    if (!eventId) {
      setEventType('');
      return;
    }
    const found = safeEvents.find((e) => e.id === eventId);
    setEventType(found?.type || '');
  }, [eventId, safeEvents]);

  // Lead (Meta Pixel)
  useEffect(() => {
    if (!FB_PIXEL_ID) return;
    if (submitStatus?.ok && !firedRef.current) {
      const advanced = agree ? { em: email, ph: phone } : undefined;
      const eventIdForPixel =
        (window.crypto as Crypto | undefined)?.randomUUID?.() ?? String(Date.now());
      initAndTrackOnce(
        FB_PIXEL_ID,
        'Lead',
        {
          eventID: eventIdForPixel,
          event_source_url: window.location.href,
          content_name: 'ZeroClub Signup',
          eventId: eventIdForPixel,
          eventType,
          eventLabel: selectedEventLabel,
          preferredChannel,
        },
        advanced
      );
      firedRef.current = true;
    }
  }, [submitStatus?.ok, agree, email, phone, preferredChannel, eventType, selectedEventLabel]);

  // -------------------------
  // HANDLE SUBMIT (GA: submit_form + error_submit)
  // -------------------------
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    // помилка: немає GAS endpoint
    if (!GAS_ENDPOINT) {
      e.preventDefault();
      setSubmitStatus({ ok: false, msg: 'No GAS endpoint (VITE_GAS_ENDPOINT).' });

      if (typeof window !== 'undefined') {
        (window as any).gtag?.('event', 'error_submit', {
          event_category: 'lead',
          event_label: selectedEventLabel || 'ZeroClub Modal',
          event_type: eventType || undefined,
          error_reason: 'no_endpoint',
        });
      }

      return;
    }

    if (!agree) {
      e.preventDefault();
      setSubmitStatus({ ok: false, msg: 'Please confirm you agree to the data notice.' });

      if (typeof window !== 'undefined') {
        (window as any).gtag?.('event', 'error_submit', {
          event_category: 'lead',
          event_label: selectedEventLabel || 'ZeroClub Modal',
          event_type: eventType || undefined,
          error_reason: 'no_consent',
        });
      }

      return;
    }

    if (!preferredChannel) {
      e.preventDefault();
      setSubmitStatus({ ok: false, msg: 'Please select where it is best to contact you.' });

      if (typeof window !== 'undefined') {
        (window as any).gtag?.('event', 'error_submit', {
          event_category: 'lead',
          event_label: selectedEventLabel || 'ZeroClub Modal',
          event_type: eventType || undefined,
          error_reason: 'no_channel',
        });
      }

      return;
    }

    if (typeof window !== 'undefined') {
      (window as any).gtag?.('event', 'submit_form', {
        event_category: 'lead',
        event_label: selectedEventLabel || 'ZeroClub Modal',
        event_type: eventType || undefined,
        submit_status: 'attempt',
      });
    }

    setSubmitting(true);
    setSubmitStatus(null);
  }

  const showEventSelect = !lockEvent;
  const eventOptions: SelectOption[] = safeEvents.map((e) => ({
    value: e.id,
    label: [e.type, e.date].filter(Boolean).join(' | ') || e.title,
  }));

  const toggleChannel = (channel: string) => {
    setPreferredChannel((prev) => (prev === channel ? '' : channel));
  };

  // -------------------------
  // CLOSE FORM (GA: close_form)
  // -------------------------
  const handleClose = () => {
    if (typeof window !== 'undefined' && openTrackedRef.current) {
      (window as any).gtag?.('event', 'close_form', {
        event_category: 'lead',
        event_label: selectedEventLabel || 'ZeroClub Modal',
        event_type: eventType || undefined,
      });
    }

    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <div
          className={clsx(
            'relative mx-auto my-4 sm:my-8 w-full max-w-[92vw] sm:max-w-2xl transition-all duration-500',
            'scale-100 opacity-100',
            'max-h-[calc(100svh-32px)] supports-[height:100dvh]:max-h-[calc(100dvh-32px)]'
          )}
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <GradientFrame>
            {/* Header */}
            <div className="relative px-5 sm:px-8 py-5 sm:py-6 border-b border-white/10 bg-gradient-to-b from-[#1a3b5c]/40 to-[#0d273d]/20">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#d65c30]/20 via-[#1a3b5c]/20 to-[#0d273d]/20 border border-white/10 flex items-center justify-center">
                  <Sparkles className="w-5 h-5" style={{ color: '#d65c30' }} />
                </div>
                <h3 className="font-montserrat font-bold text-2xl sm:text-3xl bg-[linear-gradient(90deg,#ff7a1a_0%,#ff8f2f_45%,#ffb867_100%)] bg-clip-text text-transparent">
                  Join the Next ZeroClub Event
                </h3>
              </div>
              <p className="font-montserrat text-sm" style={{ color: '#C0D6DF' }}>
                Be part of a community where ideas grow, connections form, and opportunities begin.
              </p>

              <button
                aria-label="Close"
                onClick={handleClose}
                className="absolute right-3 sm:right-4 top-3 sm:top-4 p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all duration-300"
              >
                <X className="w-5 h-5" style={{ color: '#C0D6DF' }} />
              </button>
            </div>

            <div
              className={clsx(
                'relative px-5 sm:px-8 py-5 sm:py-6 overflow-y-auto custom-scrollbar',
                'max-h-[calc(100svh-180px)] supports-[height:100dvh]:max-h-[calc(100dvh-180px)]',
                'overscroll-y-contain'
              )}
              style={{
                WebkitOverflowScrolling: 'touch',
                background:
                  'linear-gradient(to bottom, rgba(13,39,61,0.10), rgba(13,39,61,0.00))',
              }}
            >
              <iframe name="signup_iframe" ref={iframeRef} style={{ display: 'none' }} />

              <form
                ref={formRef}
                action={GAS_ENDPOINT}
                method="POST"
                target="signup_iframe"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <input type="hidden" name="sheet" value="Registrations" />

                {showEventSelect ? (
                  <div className="space-y-2">
                    <SelectInput
                      label="Event"
                      name="eventId"
                      value={eventId}
                      onChange={(e) => setEventId(e.target.value)}
                      options={eventOptions}
                      placeholderLabel="Select event"
                      required
                    />
                  </div>
                ) : (
                  <>
                    <input type="hidden" name="eventId" value={eventId} />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm" style={{ color: '#C0D6DF' }}>
                        Event:
                      </span>
                      <span
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-sm"
                        style={{ color: '#C0D6DF' }}
                      >
                        <span className="font-semibold">{selectedEventLabel}</span>
                      </span>
                    </div>
                  </>
                )}

                <input type="hidden" name="eventLabel" value={selectedEventLabel} />
                <input type="hidden" name="eventType" value={eventType} />

                <TextInput
                  label="Name & Surname"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                  ref={firstFieldRef}
                />
                <TextInput
                  label="Email Address"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@example.com"
                  required
                />
                <TextInput
                  label="Phone Number"
                  type="tel"
                  name="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (123) 123-4567"
                  required
                />

                {/* Блок із мессенджерами — ОДИН вибір */}
                <div className="space-y-3">
                  <span className="text-sm font-montserrat font-medium text-slate-300">
                    Where is it best to contact you?
                  </span>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {CONTACT_OPTIONS.map((opt) => {
                      const isSelected = preferredChannel === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => toggleChannel(opt)}
                          className={clsx(
                            'relative px-4 py-2.5 rounded-xl font-montserrat text-sm transition-all duration-200 text-left flex items-center gap-2',
                            'border-2 focus:outline-none',
                            isSelected
                              ? 'bg-gradient-to-r from-[#f97316]/20 to-[#ec4899]/20 border-[#ff8a4a]/55 text-white'
                              : 'bg-[rgba(18,40,64,0.9)] border-[rgba(255,255,255,0.14)] text-slate-100 hover:bg-[rgba(24,50,80,0.95)] hover:border-[rgba(255,255,255,0.22)]'
                          )}
                        >
                          {isSelected && (
                            <CheckCircle2 className="w-4 h-4 text-[#ff8a4a] flex-shrink-0" />
                          )}
                          <span className="flex-1">{opt}</span>
                        </button>
                      );
                    })}
                  </div>

                  <input type="hidden" name="preferredChannel" value={preferredChannel} />
                </div>

                <PrivacyCheckbox
                  checked={agree}
                  onChange={setAgree}
                  label={
                    <>
                      I agree to send my data to ZeroClub for event participation purposes.
                      We will never share your contact details.
                    </>
                  }
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className={clsx(
                    'w-full py-4 rounded-xl font-montserrat font-bold text-white text-lg',
                    'shadow-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]',
                    'flex items-center justify-center gap-2',
                    submitting && 'opacity-70 cursor-not-allowed'
                  )}
                  style={{
                    background: '#d65c30',
                    boxShadow: '0 10px 30px -5px rgba(214,92,48,0.45)',
                  }}
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>Reserve My Spot</>
                  )}
                </button>

                {submitStatus && (
                  <div
                    className={clsx(
                      'rounded-xl px-4 py-3 text-sm font-montserrat flex items-start gap-2 border'
                    )}
                    style={
                      submitStatus.ok
                        ? {
                            background: 'rgba(12, 68, 48, 0.2)',
                            borderColor: 'rgba(46, 204, 113, 0.35)',
                            color: '#2ecc71',
                          }
                        : {
                            background: 'rgba(86, 24, 24, 0.2)',
                            borderColor: 'rgba(214, 92, 48, 0.5)',
                            color: '#d65c30',
                          }
                    }
                  >
                    <span>{submitStatus.msg}</span>
                  </div>
                )}
              </form>
            </div>
          </GradientFrame>
        </div>

        <style>{`
          @media (min-width: 640px) {
            .custom-scrollbar::-webkit-scrollbar { width: 10px; }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: linear-gradient(180deg, rgba(26,59,92,0.25), rgba(13,39,61,0.15));
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: linear-gradient(180deg, rgba(214,92,48,0.85), rgba(250, 171, 0, 0.75));
              border-radius: 10px;
              border: 1px solid rgba(255,255,255,0.15);
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: linear-gradient(180deg, rgba(214,92,48,1), rgba(192,214,223,0.95));
            }
          }
        `}</style>
      </Modal>

      <SlideInToast
        open={toastOpen}
        onClose={() => setToastOpen(false)}
        variant={submitStatus?.ok ? 'success' : submitStatus ? 'error' : undefined}
        title={submitStatus?.ok ? 'Submitted' : submitStatus ? 'Error' : undefined}
        message={submitStatus?.msg}
      />
    </>
  );
}
